/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useEffect, useState, FC } from 'react';
import styled from 'styled-components';
import { compact, isEmpty, isUndefined } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';
import {
  TableCell as DefTableCell,
  TableRow as DefTableRow,
  TableRowProps,
} from '@mui/material';
import { AdminUsersSortings } from '../../utils/admin-consts.ts';
import { LocalStorage } from '../../utils/consts';
import { OrderByDirection, UsersOrderByField } from '../../apollo';
import { Checkbox, CheckboxProps } from '../form-elements';
import { AdminUserTableColumnName } from '../tables';
import { SortLink } from '../texts';
import {
  AdminTableEmailCell,
  type AdminTableEmailCellProps,
} from './AdminTableEmailCell';

type AdminTableHeadRowProps = TableRowProps & {
  columns: AdminUserTableColumnName[];
  sortableColumns?: AdminUserTableColumnName[];
  selectAllCheckboxProps: CheckboxProps;
  onSortingsChange: (sortings: AdminUsersSortings) => void;
};

const AdminTableHeadRow = ({
  columns,
  sortableColumns = [],
  selectAllCheckboxProps,
  onSortingsChange,
  ...props
}: AdminTableHeadRowProps) => {
  const { t } = useTranslation();

  const [initialSortings, setInitialSortings] = useLocalStorage<
    Array<{ field: AdminUserTableColumnName; value: OrderByDirection }>
  >(LocalStorage.adminUsersTableSortings);

  const [sortings, setSortings] = useState<
    Array<{
      field: AdminUserTableColumnName;
      value: OrderByDirection;
    }>
  >(() => {
    if (isEmpty(initialSortings)) {
      return [
        {
          field: 'dateOfSignup',
          value: OrderByDirection.Desc,
        },
      ];
    }
    return initialSortings!;
  });

  useEffect(() => {
    setInitialSortings(sortings);
  }, [sortings, setInitialSortings]);

  useEffect(() => {
    onSortingsChange(
      compact(
        sortings.map(({ field, value }) => {
          const sortField = sortFieldsByColumnName[field];
          if (!sortField) return null;
          return {
            Field: sortField,
            Direction: value,
          };
        })
      )
    );
  }, [sortings, onSortingsChange]);

  const onSortingChange = (key: AdminUserTableColumnName) => {
    const currentValue = sortings.find(({ field }) => field === key)?.value;
    let nextValue;

    switch (currentValue) {
      case OrderByDirection.Desc:
        nextValue = OrderByDirection.Asc;
        break;
      case OrderByDirection.Asc:
        nextValue = undefined;
        break;
      default:
        nextValue = OrderByDirection.Desc;
    }

    /*const nextSortings = sortings.filter(({ field }) => field !== key);

    if (nextValue) {
      nextSortings.push({
        field: key,
        value: nextValue,
      });
    }*/

    setSortings(
      !isUndefined(nextValue)
        ? [
            {
              field: key,
              value: nextValue,
            },
          ]
        : []
    );
  };

  const getSortDirection = (key: AdminUserTableColumnName) =>
    sortings.find(({ field }) => field === key)?.value;

  return (
    <TableRow {...props}>
      <TableCell>
        <Checkbox
          inputProps={{ 'aria-label': 'select all desserts' }}
          {...selectAllCheckboxProps}
        />
      </TableCell>
      {columns.map((key, index) => {
        let Cell: FC<AdminTableEmailCellProps> = TableCell;
        if (key === 'email') {
          Cell = AdminTableEmailCell;
        }
        const cellText = t(columnsTitles[key]!);
        let cellValue;

        if (sortableColumns.includes(key)) {
          cellValue = (
            <SortLink
              direction={getSortDirection(key)}
              onClick={() => onSortingChange(key)}
            >
              {cellText}
            </SortLink>
          );
        } else {
          cellValue = cellText;
        }

        return <Cell key={index}>{cellValue}</Cell>;
      })}
    </TableRow>
  );
};

const TableRow = styled(DefTableRow)``;
const TableCell = styled(DefTableCell)``;

const sortFieldsByColumnName: Partial<{
  [key in AdminUserTableColumnName]: UsersOrderByField;
}> = {
  exchangesAmounts: UsersOrderByField.AssetsUnderManagement,
  dateOfSignup: UsersOrderByField.DateOfSignup,
  lastIndexUpdateDate: UsersOrderByField.RebalancingDate,
  paid: UsersOrderByField.Kosten,
};

const columnsTitles: Partial<{
  [key in AdminUserTableColumnName]: string;
}> = {
  id: 'ADMIN_ALL_USERS_PAGE__tableNameColumnID',
  name: 'ADMIN_ALL_USERS_PAGE__tableNameColumnName',
  amountToInvest: 'ADMIN_ALL_USERS_PAGE__tableAmountToInvestColumnName',
  email: 'ADMIN_ALL_USERS_PAGE__tableEmailColumnName',
  phone: 'ADMIN_ALL_USERS_PAGE__tablePhoneColumnName',
  customStatuses: 'ADMIN_ALL_USERS_PAGE__tableStatusesColumnName',
  exchangesStatuses: 'ADMIN_ALL_USERS_PAGE__tableExchangesStatusesColumnName',
  lastIndexUpdateDate:
    'ADMIN_ALL_USERS_PAGE__tableLastIndexUpdateDateColumnName',
  dateOfSignup: 'ADMIN_ALL_USERS_PAGE__tableSignupDateColumnName',
  exchangesAmounts: 'ADMIN_ALL_USERS_PAGE__tableMoneyUnderManagementColumnName',
  paid: 'ADMIN_ALL_USERS_PAGE__tableMoneyPaidColumnName',
  tfa: 'ADMIN_ALL_USERS_PAGE__table2FAColumnName',
  deactivateSubscription: 'ADMIN_ALL_USERS_PAGE__tableDeactivateColumnName',
  rebalance: 'ADMIN_ALL_USERS_PAGE__tableRebalanceColumnName',
  keysRemovement: 'ADMIN_ALL_USERS_PAGE__tableKeysRemovementColumnName',
};

export { AdminTableHeadRow };
